<template>
  <SuccessErrorModal
    :show-modal="showModal"
    :title="modalData.title"
    :description="modalData.description"
    :isError="modalData.isError"
    @close="showModal = false"
  />

  <Dialog :open="showSuccessModal" @update:open="showSuccessModal = $event">
    <DialogContent class="max-w-[500px] rounded-xl bg-teal-500 border-none text-white top-[10%] translate-y-0">
      <button
        @click="showSuccessModal = false"
        class="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
      >
        <X class="h-4 w-4" />
      </button>
      <div class="flex flex-col items-center justify-center py-8 text-center">
        <div class="mb-6 rounded-full border-2 border-white p-3">
          <Check class="h-8 w-8" />
        </div>
        <p class="text-xl font-medium">{{ successMessage }}</p>
      </div>
    </DialogContent>
  </Dialog>

  <Dialog :open="showEmailDialog" @update:open="showEmailDialog = $event">
    <DialogContent class="max-w-[400px] rounded-xl">
      <DialogHeader>
        <DialogTitle>Enter Email Address</DialogTitle>
        <DialogDescription>Please enter your email address to receive the statement.</DialogDescription>
      </DialogHeader>
      <div class="space-y-4">
        <Input v-model="emailInput" type="email" placeholder="email@example.com" />
        <div class="flex justify-end gap-2">
          <Button variant="outline" @click="showEmailDialog = false">Cancel</Button>
          <Button @click="sendWithEmail" :disabled="!isValidEmail || isSending" class="max-w-[100px]">
            <Loader2 v-if="isSending" class="h-4 w-4 animate-spin mr-2" />
            Send
          </Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>

  <Card class="rounded-2xl p-0 shadow-md">
    <CardHeader class="flex flex-row items-center justify-between p-6">
      <CardTitle class="text-[20px] font-semibold">Statement</CardTitle>
      <div class="flex gap-2">
        <Button
          variant="outline"
          size="icon"
          class="rounded-lg"
          @click="sendStatementEmail"
          :disabled="isSending || noPayments"
        >
          <Loader2 v-if="isSending" class="h-4 w-4 animate-spin" />
          <Mail v-else class="h-4 w-4" />
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger as-child>
            <Button variant="outline" size="icon" class="rounded-lg" :disabled="noPayments || isExporting">
              <Loader2 v-if="isExporting" class="h-4 w-4 animate-spin" />
              <Upload v-else class="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem @click="exportPaymentsToCSV">Download CSV</DropdownMenuItem>
            <DropdownMenuItem @click="downloadOrderStatement">Download PDF</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </CardHeader>
    <CardContent class="p-0">
      <div v-if="noPayments" class="p-6">
        <p class="text-center text-sm text-grey-light-400">No payments to displayed for {{ order?.state }} order</p>
      </div>
      <Table v-else>
        <TableHeader>
          <TableRow class="bg-grey-light-50 border-t">
            <TableHead class="text-[14px] font-medium text-grey-light-700 pl-6">Date</TableHead>
            <TableHead class="text-[14px] font-medium text-grey-light-700 text-right">Amount</TableHead>
            <TableHead v-if="!isMobileOrIsTable" class="text-[14px] font-medium text-grey-light-700">Type</TableHead>
            <TableHead v-if="!isMobileOrIsTable" class="text-[14px] font-medium text-grey-light-700">Method</TableHead>
            <TableHead class="text-[14px] font-medium text-grey-light-700 pr-6">Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <TableRow v-for="payment in payments" :key="payment.id" class="border-b border-grey-light-100">
            <TableCell class="text-[14px] text-grey-light-900 pl-6 font-medium">{{
              formatDateShort(payment.date)
            }}</TableCell>
            <TableCell class="text-[14px] text-grey-light-600 text-right">{{
              displayCurrency(payment.amount)
            }}</TableCell>
            <TableCell v-if="!isMobileOrIsTable" class="text-[14px] text-grey-light-600">{{
              capitalize(payment.type || 'Contribution')
            }}</TableCell>
            <TableCell v-if="!isMobileOrIsTable" class="text-[14px] text-grey-light-600">{{
              payment.method
            }}</TableCell>
            <TableCell class="pr-6">
              <Badge
                :variant="getPaymentStatusVariant(payment.status)"
                class="rounded-full text-grey-light-600 font-medium"
                :class="getPaymentStatusClasses(payment.status)"
              >
                {{ payment.status }}
              </Badge>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </CardContent>
  </Card>
</template>

<script setup lang="ts">
import SuccessErrorModal from '@/components/common/SuccessErrorModal.vue';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import type { IPaymentReport } from '@/models/payments';
import { DownloadStatement, SendStatement } from '@/services/statement';
import { useUserStore } from '@/stores/users';
import { displayCurrency, formatDateShort } from '@/utils/format.utils';
import { getPaymentStatusClasses, getPaymentStatusVariant } from '@/utils/payment.utils';
import { generatePaymentsCsv } from '@/utils/paymentPlan.utils';
import { useBreakpoint } from '@/utils/useBreakPoint';
import { capitalize } from 'lodash';
import { Check, Loader2, Mail, Upload, X } from 'lucide-vue-next';
import { computed, ref } from 'vue';
import type { ModelOrderResponse } from '@layup/api-sdk';

const props = defineProps<{
  payments: IPaymentReport[];
  order?: ModelOrderResponse;
}>();

const { user } = useUserStore();
const { isMobile, isTablet } = useBreakpoint();
const isMobileOrIsTable = computed(() => isMobile.value || isTablet.value);

const showModal = ref(false);
const showEmailDialog = ref(false);
const showSuccessModal = ref(false);
const successMessage = ref('');
const emailInput = ref('');
const isSending = ref(false);
const isExporting = ref(false);
const modalData = ref({
  title: '',
  description: '',
  isError: false,
});

const noPayments = computed(() => !props.payments || props.payments.length === 0);

const isValidEmail = computed(() => {
  if (!emailInput.value) return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(emailInput.value);
});

const sendStatementEmail = async () => {
  if (!props.payments || props.payments.length === 0) {
    modalData.value = {
      title: 'No Data Available',
      description: 'No payment data available to send',
      isError: true,
    };
    showModal.value = true;
    return;
  }

  if (!user.email) {
    showEmailDialog.value = true;
    return;
  }

  await sendStatement(user.email);
};

const sendWithEmail = async () => {
  if (!emailInput.value) return;
  await sendStatement(emailInput.value);
  showEmailDialog.value = false;
  emailInput.value = '';
};

const sendStatement = async (email: string) => {
  try {
    isSending.value = true;
    await SendStatement(props.order?._id?.toString()!, email);
    successMessage.value = `You have successfully sent your order statement to ${email}`;
    showSuccessModal.value = true;
  } catch (error) {
    modalData.value = {
      title: 'Failed to Send',
      description: 'Failed to send statement email',
      isError: true,
    };
    showModal.value = true;
  } finally {
    isSending.value = false;
  }
};

const downloadOrderStatement = async () => {
  if (!props.order?._id) return;

  isExporting.value = true;
  try {
    const blob = await DownloadStatement(props.order._id);
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Order statement - ${props.order._id}.pdf`);
    link.click();
    URL.revokeObjectURL(url);
  } catch (error) {
    modalData.value = {
      title: 'Failed to Download',
      description: 'Failed to downloading statement email',
      isError: true,
    };
    showModal.value = true;
  } finally {
    isExporting.value = false;
  }
};

const exportPaymentsToCSV = () => {
  if (!props.payments || props.payments.length === 0) {
    console.warn('No payment data available for export');
    return;
  }

  isExporting.value = true;

  try {
    if (!props.order?._id) return;

    const csvContent = generatePaymentsCsv(props.payments);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Order statement - ${props.order._id}.csv`);
    link.click();
    URL.revokeObjectURL(url);
  } catch (error) {
    modalData.value = {
      title: 'Failed to Download',
      description: 'Failed to downloading statement email',
      isError: true,
    };
    showModal.value = true;
  } finally {
    isExporting.value = false;
  }
};
</script>
